package dev.youndie.site.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initTelekPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("telek.md", FrontMatterElement.Builder { addScalar("title", "telek"); addScalar("searchTitle", "Telegram bot framework for Kotlin — finite state machine, JVM and native"); addScalar("description", "Telegram bots as a state machine rather than a pile of callbacks. Pure transitions, effects on the side, two transports, published for the JVM and native Linux."); addScalar("library", "telek"); addScalar("post", "/blog/bots-as-state-machines"); addScalar("docs", "https://youndie.github.io/telek/"); }))
}

@Page("/telek")
@Layout(".components.layouts.LibraryLayout")
@Composable
fun TelekPage() {
    org.jetbrains.compose.web.dom.H2(attrs = { id("why-it-exists") }) {
        org.jetbrains.compose.web.dom.Text("Why it exists")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Every Telegram bot starts as a ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("when")
        }
        org.jetbrains.compose.web.dom.Text(" over the incoming message and ends as callbacks with a nullable ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("awaitingCity")
        }
        org.jetbrains.compose.web.dom.Text(" field in a map somewhere. Not laziness — the framework hands you an event and no place to put the fact that this user is halfway through something.")
    }
    com.varabyte.kobweb.compose.dom.GenericTag("blockquote") {
        org.jetbrains.compose.web.dom.P {
            org.jetbrains.compose.web.dom.Text("state + input → newState + effects")
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-that-buys") }) {
        org.jetbrains.compose.web.dom.Text("What that buys")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A transition is a function: current state and input in, next state and a list of effects out. It performs none of them. From that one split everything else follows —")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("a transition is testable with no bot, no network and no mocks;")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("a conversation replays from its inputs, because nothing hid in a closure;")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("persisting a wizard is persisting a ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("value")
            }
            org.jetbrains.compose.web.dom.Text(", which is what makes it survive a restart.")
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("two-transports-same-shape") }) {
        org.jetbrains.compose.web.dom.Text("Two transports, same shape")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("""repositories { maven("https://reposilite.kotlin.website/snapshots") }

dependencies {
    implementation("io.github.youndie.telek:core:<VERSION>")

    // pick one:
    implementation("io.github.youndie.telek:telegram:<VERSION>") // kotlin-telegram-bot
    // implementation("io.github.youndie.telek:ktg:<VERSION>")   // ktgbotapi
}
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Swapping transports is only possible because the core never touches the network. If transitions did their own sends, the transport would be baked into every one of them.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("native-because-the-core-is-pure") }) {
        org.jetbrains.compose.web.dom.Text("Native, because the core is pure")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("core")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("ktg")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("router")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("router-ktg")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("persistence")
        }
        org.jetbrains.compose.web.dom.Text(" and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("testing")
        }
        org.jetbrains.compose.web.dom.Text(" publish for ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("JVM, linuxX64 and linuxArm64")
        }
        org.jetbrains.compose.web.dom.Text(", so a bot can ship as a single native binary. ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("telegram")
        }
        org.jetbrains.compose.web.dom.Text(" and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("router-telegram")
        }
        org.jetbrains.compose.web.dom.Text(" stay JVM-only, because kotlin-telegram-bot is — the honest shape of the dependency rather than a limitation to work around.")
    }
}
